import { AssetSubClassName, Entity, MappedEntity } from '@api';
import { PriceHoverData, TimeSeriesItem } from '@chart';
import type { PriceChangeParams } from '@helpers';
import { EST_TIME_ZONE, getPriceChange } from '@helpers';
import {
  getLivePriceDataKey,
  LivePriceData,
  LivePriceInfo,
  mapLivePriceData,
  PriceEntity,
} from '@terminal/hooks/use-live-price';

export type { PriceChangeParams };
export { EST_TIME_ZONE, getPriceChange };

export function getTickerForLivePrice(
  entity: Pick<Entity, 'subscribable_ticker'>
) {
  return entity.subscribable_ticker;
}

export const isPriceSnake = (entity: MappedEntity, snakeName = '') =>
  snakeName === entity.default_snake;

export const isFX = (subClass: string) =>
  subClass === AssetSubClassName.MajorCross ||
  subClass === AssetSubClassName.GenericCross;

export const getPriceData = ({
  hoverData,
  livePriceData,
  entity,
  ts,
  priceEntity,
}: {
  hoverData?: PriceHoverData;
  livePriceData: LivePriceData;
  entity: MappedEntity;
  ts: TimeSeriesItem[];
  priceEntity?: PriceEntity;
}): LivePriceInfo => {
  const entityWithCountry = {
    ...entity,
    trading_country: entity.trading_country,
  };
  const livePriceDataKey = getLivePriceDataKey(entity) ?? '';

  if (hoverData?.currentPrice && hoverData?.previousPrice) {
    return mapLivePriceData({
      entity: entityWithCountry,
      lastKnownPrice: hoverData.currentPrice.close,
      priceBefore:
        hoverData.previousPrice.close || hoverData.currentPrice.close,
      time: hoverData.currentPrice.time,
    });
  }

  if (!livePriceData[livePriceDataKey] && ts.length) {
    return mapLivePriceData({
      entity: entityWithCountry,
      lastKnownPrice: ts[ts.length - 1].close,
      priceBefore: ts[ts.length - 2].close || ts[ts.length - 1].close,
      time: ts[ts.length - 1].time,
    });
  }

  if (!livePriceData[livePriceDataKey] && priceEntity) {
    return mapLivePriceData({
      entity: entityWithCountry,
      lastKnownPrice: priceEntity.lastKnownPrice,
      priceBefore: priceEntity.priceBefore,
      time: priceEntity.time,
    });
  }

  return livePriceData[livePriceDataKey];
};
